import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSmallScreen from 'hooks/useSmallScreen';
import DialogContent from 'housecall-ui/dist/DesignSystem/Feedback/Dialog/DialogContent';
import CircularProgress from 'housecall-ui/dist/DesignSystem/Feedback/Progress/CircularProgress';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';

import { withStripe } from 'components/Common/Form/Stripe/Utils';
import { hideIntercom } from 'components/Common/Intercom/IntercomUtils';
import PayWallActionsLayout from 'components/PayWall/PayWallActionsLayout';
import PayWallDialogTitle from 'components/PayWall/PayWallDialogTitle';
import PayWallPaymentReview from 'components/PayWall/PayWallPaymentReview';
import { updatePlanAndCharge, updatePlanNoCharge } from 'components/PayWall/Redux/Actions';
import { UNSUPPORTED } from 'components/PayWall/Redux/Data';
import { PayWallFullState } from 'components/PayWall/Redux/Reducer.type';
import { getPaywallSpinnerState } from 'components/PayWall/Redux/Selectors';
import UpdatePlanNoChargeButton from 'components/PayWall/UpdatePlanNoChargeButton';
import { useTrackEventsService } from 'components/Services/Analytics/useTrackEventsService';
import useHasPrimaryCard from 'components/Settings/Billing/CreditCards/hooks/useHasPrimaryCard';

import BackButton from './PayWallBackButton';
import CancelButton from './PayWallCancelButton';
import { PayWallContext } from './PayWallContextProvider';
import {
  CIRCULAR_PROGRESS_BUTTON_SIZE,
  PAY_WALL_VERSION,
  useButtonProgressStyles,
  useDialogContentStyles,
} from './PayWallDialog';
import { useOrgAddressFormInvalid } from './PayWallEditableAddress';
import SubmitButton from './PayWallSubmitButton';

function useShouldDisableButtons() {
  const showSpinner = useSelector(getPaywallSpinnerState);
  const isAddressInvalid = useOrgAddressFormInvalid();
  const whenToApplyChange = useSelector((state: PayWallFullState) => state.payWall.planSummaryPreview?.when_to_apply);
  const { showCreditCardForm } = useContext(PayWallContext);

  const hasPrimaryCreditCard = useHasPrimaryCard();

  const isProImpersonated = useSelector((state: PayWallFullState) => state.payWall.isProImpersonated);

  if (isAddressInvalid) {
    return true;
  }

  if (showSpinner) {
    return true;
  }

  if (whenToApplyChange === UNSUPPORTED && !isProImpersonated) {
    return true;
  }

  if (showCreditCardForm) {
    return true;
  }

  return !hasPrimaryCreditCard;
}

function useIsCurrentlyInPayingPlan() {
  const originalCurrentPlan = useSelector((state: PayWallFullState) => state.payWall.originalCurrentPlan);
  return Number(originalCurrentPlan.rate_flat_monthly) > 0;
}

function useHideIntercomForSmallScreen() {
  const isSmallScreen = useSmallScreen();
  useEffect(() => {
    if (isSmallScreen) {
      hideIntercom();
    }
  }, [isSmallScreen]);
}

function useOnSubmit(): [() => Promise<void>, string] {
  const dispatch = useDispatch();
  const { trackButtonClicked } = useTrackEventsService();

  const [stripeError, setStripeError] = useState('');

  const submit = async () => {
    trackButtonClicked({ buttonName: 'pay now', payWallVersion: PAY_WALL_VERSION });
    setStripeError('');

    dispatch(updatePlanAndCharge());
  };

  return [submit, stripeError];
}

export type PayWallSubmitTrigger = 'charge' | 'nocharge';

type Props = {
  onCancel: () => void;
  onBack: () => void;
};

function PayWallReviewDialog(props: Props): JSX.Element {
  const { onCancel, onBack } = props;

  useHideIntercomForSmallScreen();
  const dispatch = useDispatch();
  const [submit, stripeErrorMessage] = useOnSubmit();
  const shouldDisableButtons = useShouldDisableButtons();
  const isCurrentlyInPayingPlan = useIsCurrentlyInPayingPlan();
  const dialogContentClasses = useDialogContentStyles({ isLoading: false });
  const loading = useSelector(getPaywallSpinnerState);
  const classes = useButtonProgressStyles({ circularProgressSize: CIRCULAR_PROGRESS_BUTTON_SIZE });
  const [submitTrigger, setSubmitTrigger] = useState<PayWallSubmitTrigger>('charge');

  const handleSubmit = () => {
    setSubmitTrigger('charge');
    submit();
  };

  const handleSubmitNoCharge = () => {
    setSubmitTrigger('nocharge');
    dispatch(updatePlanNoCharge());
  };

  const submitButton = (
    <Box className={classes.wrapper}>
      <SubmitButton disabled={shouldDisableButtons} onClick={handleSubmit}>
        {isCurrentlyInPayingPlan ? 'Update plan' : 'Pay now'}
      </SubmitButton>
      {loading && submitTrigger === 'charge' && (
        <CircularProgress className={classes.buttonProgress} size={CIRCULAR_PROGRESS_BUTTON_SIZE} />
      )}
    </Box>
  );

  return (
    <>
      <PayWallDialogTitle>Review & pay</PayWallDialogTitle>
      <DialogContent dividers={false} classes={dialogContentClasses}>
        <PayWallPaymentReview stripeErrorMessage={stripeErrorMessage} />
      </DialogContent>
      <PayWallActionsLayout
        submitButton={submitButton}
        cancelButton={<CancelButton onClick={onCancel} />}
        backButton={<BackButton onClick={onBack} />}
        noChargeButton={
          <UpdatePlanNoChargeButton
            isCurrentlyPaying={isCurrentlyInPayingPlan}
            submitTrigger={submitTrigger}
            onSubmit={handleSubmitNoCharge}
          />
        }
      />
    </>
  );
}

export default withStripe()(PayWallReviewDialog);
